import {FC, ReactNode, useEffect, useState} from 'react'
import {gql} from '@apollo/client'
import {Controller, FormProvider, SubmitHandler, useForm} from 'react-hook-form'
import {yup} from 'libs/yup'
import {yupResolver} from '@hookform/resolvers/yup'
import Button from 'jbc-front/components/Button'
import {useMutation, useQuery} from 'hooks/graphql'
import {EmployeeName} from 'pages/withAuth/adminPage/procedures/parts/EmployeeName'
import {ProcedureContext, ReportFormProps} from './ReportForm'
import {useConfirmUpgrade} from './useConfirmUpgrade'
import {FormErrors} from 'components/reactHookForm/FormErrors'
import {Panel, Body, Head} from 'components/ui/Panel'
import {laborConsultantSchema, LaborConsultantSection} from './LaborConsultant'
import {DateField} from 'components/reactHookForm/DateField'
import {Label} from 'components/forms/Label'
import {Radio, RadioContainer} from 'jbc-front/components/presenters/form/Radio'
import styles from './R511Report.scss'
import {dateFormat, fullNameFormat} from 'libs/formatter'
import {InputWithText} from 'jbc-front/components/presenters/form/InputWithText'
import {ErrorMessage} from 'jbc-front/components/presenters/form/ErrorMessage'
import {Select} from 'jbc-front/components/presenters/form/Select'
import {Checkbox} from 'jbc-front/components/presenters/form/Checkbox'
import {Note} from 'jbc-front/components/presenters/ui/Note'
import {Input} from 'jbc-front/components/presenters/form/Input'
import {Textarea} from 'jbc-front/components/presenters/form/Textarea'
import {useDispatch} from 'react-redux'
import {asyncError} from 'store/actions/asyncError'
import {ChildcareMethod} from 'features/procedures/Flow/ChildcareMethodSelect/query'
import {
  PAST_CHILDCARE_PROCEDURES_FOR_REFLECT,
  PastChildcareProceduresForReflectQueryResult,
  PastChildcareProceduresForReflectQueryVariables,
} from 'features/procedures/Flow/ChildcareInfoForm/query'

// @ts-ignore
import {titleMap} from 'procedureStatuses/Show'

const R511_REPORT = gql`
  query R511Report($procedureStatusId: ID!, $procedureTmpType: ProcedureTmpType!) {
    client {
      id
      procedureStatus(procedureStatusId: $procedureStatusId) {
        id
        procedureType
        employee {
          id
          staffCode
          displayFullName
        }
        childcareMethodSelect: procedureTmpDatum(type: $procedureTmpType, name: "childcare_method_select") {
          id
          data
        }
        r511ParamSet {
          id
          startedOn
          birthDate
          returnToWorkOn
          isPapaMamaLeavePlus
          spouseInsurancePersonNumber0
          spouseInsurancePersonNumber1
          spouseInsurancePersonNumber2
          specialReport1
          specialReport2
          submitOn
          ownerCertifiedOn
          isApplicantConsented
          startOn1
          endOn1
          workDays1
          workHours1
          salaryAmount1
          startOn2
          endOn2
          workDays2
          workHours2
          salaryAmount2
          startOn3
          endOn3
          workDays3
          workHours3
          salaryAmount3
          reasonForExtendingPaymentPeriod
          extendedPaymentPeriodStartOn
          extendedPaymentPeriodEndOn
          hasCommutingExpenses
          commutingExpensePaymentDurationUnit
          commutingExpensePaymentDurationUnitOtherDetail
          hasContractPeriod
          contractPeriodEndOn
          notes
          needLaborConsultant
          laborConsultantCreateDate
          laborConsultantName
          laborConsultantAgentType
          laborConsultantPrefecture {
            id
          }
          laborConsultantPhoneNumber0
          laborConsultantPhoneNumber1
          laborConsultantPhoneNumber2
          version
          needUpgrade
        }
      }
    }
  }
`

interface R511ReportQueryVariables {
  procedureStatusId: string
  procedureTmpType: 'draft' | 'employee_draft'
}

interface R511ReportQueryResult {
  client: {
    id: string
    procedureStatus: {
      id: string
      procedureType: string
      employee: {
        id: string
        staffCode: string
        displayFullName: string
      }
      childcareMethodSelect?: {
        id: string
        data: {method?: ChildcareMethod}
      }
      r511ParamSet: {
        id?: string
        startedOn?: string
        birthDate?: string
        returnToWorkOn?: string
        isPapaMamaLeavePlus: boolean
        spouseInsurancePersonNumber0?: string
        spouseInsurancePersonNumber1?: string
        spouseInsurancePersonNumber2?: string
        specialReport1?: string
        specialReport2?: string
        submitOn?: string
        ownerCertifiedOn?: string
        isApplicantConsented?: boolean
        startOn1?: string
        endOn1?: string
        workDays1?: number
        workHours1?: number
        salaryAmount1?: number
        startOn2?: string
        endOn2?: string
        workDays2?: number
        workHours2?: number
        salaryAmount2?: number
        startOn3?: string
        endOn3?: string
        workDays3?: number
        workHours3?: number
        salaryAmount3?: number
        reasonForExtendingPaymentPeriod?: string
        extendedPaymentPeriodStartOn?: string
        extendedPaymentPeriodEndOn?: string
        hasCommutingExpenses: boolean
        commutingExpensePaymentDurationUnit?: 'per_month' | 'per_3months' | 'per_6months' | 'other'
        commutingExpensePaymentDurationUnitOtherDetail?: string
        hasContractPeriod: boolean
        contractPeriodEndOn?: string
        notes: string
        needLaborConsultant: boolean
        laborConsultantCreateDate?: string
        laborConsultantName?: string
        laborConsultantAgentType?: 'submission_agent' | 'office_agent'
        laborConsultantPrefecture?: {
          id: string
        }
        laborConsultantPhoneNumber0?: string
        laborConsultantPhoneNumber1?: string
        laborConsultantPhoneNumber2?: string
        version: number
        needUpgrade: boolean
      }
    }
  }
}

const UPDATE_R511_PARAM_SET = gql`
  mutation UpdateR511ParamSet($input: UpdateR511ParamSetInput!) {
    updateR511ParamSet(input: $input) {
      r511ParamSet {
        id
      }
    }
  }
`

interface UpdateR511ParamSetQueryVariables {
  input: {
    procedureStatusId: string
    attributes: {
      startedOn: string
      birthDate?: string | null
      expectedBirthOn?: string | null
      returnToWorkOn?: string | null
      isPapaMamaLeavePlus?: boolean | null
      spouseInsurancePersonNumber0?: string | null
      spouseInsurancePersonNumber1?: string | null
      spouseInsurancePersonNumber2?: string | null
      specialReport1?: string | null
      specialReport2?: string | null
      submitOn?: string | null
      ownerCertifiedOn?: string | null
      isApplicantConsented?: boolean | null
      startOn1?: string | null
      endOn1?: string | null
      workDays1?: number | null
      workHours1?: number | null
      salaryAmount1?: number | null
      startOn2?: string | null
      endOn2?: string | null
      workDays2?: number | null
      workHours2?: number | null
      salaryAmount2?: number | null
      startOn3?: string | null
      endOn3?: string | null
      workDays3?: number | null
      workHours3?: number | null
      salaryAmount3?: number | null
      reasonForExtendingPaymentPeriod?: string | null
      extendedPaymentPeriodStartOn?: string | null
      extendedPaymentPeriodEndOn?: string | null
      hasCommutingExpenses?: boolean | null
      commutingExpensePaymentDurationUnit?: string | null
      commutingExpensePaymentDurationUnitOtherDetail?: string | null
      hasContractPeriod?: boolean | null
      contractPeriodEndOn?: string | null
      notes?: string | null
      needLaborConsultant?: boolean | null
      laborConsultantCreateDate?: string | null
      laborConsultantName?: string | null
      laborConsultantAgentType?: 'submission_agent' | 'office_agent' | null
      laborConsultantPrefectureId?: string | null
      laborConsultantPhoneNumber0?: string | null
      laborConsultantPhoneNumber1?: string | null
      laborConsultantPhoneNumber2?: string | null
      version?: number | null
    }
  }
}

const UPDATE_PROCEDURE_STATUS = gql`
  mutation updateProcedureStatus($input: UpdateProcedureStatusInput!) {
    updateProcedureStatus(input: $input) {
      procedureStatus {
        id
      }
    }
  }
`

interface UpdateProcedureStatusQueryVariables {
  input: {
    id: string
    initialized?: boolean
  }
}

export const R511Report: FC<ReportFormProps> = ({
  procedureStatusId,
  submitText,
  afterSubmit,
  procedureContext,
  comments,
  renderReject,
}) => {
  const dispatch = useDispatch()
  const {data, loading} = useQuery<R511ReportQueryResult, R511ReportQueryVariables>(R511_REPORT, {
    variables: {
      procedureStatusId,
      procedureTmpType: procedureContext === 'admin' ? 'draft' : 'employee_draft',
    },
    fetchPolicy: 'network-only',
  })
  const procedureStatus = data?.client?.procedureStatus

  const [updateR511ParamSet] = useMutation<unknown, UpdateR511ParamSetQueryVariables>(UPDATE_R511_PARAM_SET)
  const [updateProcedureStatus] = useMutation<unknown, UpdateProcedureStatusQueryVariables>(UPDATE_PROCEDURE_STATUS)

  if (loading || !procedureStatus) {
    return null
  }

  const onSubmit: SubmitHandler<Schema> = async (data) => {
    const blankToNull = (value: string | undefined | null) => (value ? value : null)

    try {
      await updateR511ParamSet({
        variables: {
          input: {
            procedureStatusId,
            attributes: {
              startedOn: data.startedOn,
              birthDate: blankToNull(data.birthDate),
              expectedBirthOn: blankToNull(data.expectedBirthOn),
              returnToWorkOn: blankToNull(data.returnToWorkOn),
              isPapaMamaLeavePlus: data.isPapaMamaLeavePlus === 'true',
              spouseInsurancePersonNumber0: data.spouseInsurancePersonNumber0,
              spouseInsurancePersonNumber1: data.spouseInsurancePersonNumber1,
              spouseInsurancePersonNumber2: data.spouseInsurancePersonNumber2,
              specialReport1: data.specialReport1,
              specialReport2: data.specialReport2,
              submitOn: blankToNull(data.remarks.submitOn),
              ownerCertifiedOn: blankToNull(data.remarks.ownerCertifiedOn),
              isApplicantConsented: !!data.remarks.isApplicantConsented,
              startOn1: blankToNull(data.paymentPeriod1.startOn),
              endOn1: blankToNull(data.paymentPeriod1.endOn),
              workDays1: data.paymentPeriod1.workDays ? parseInt(data.paymentPeriod1.workDays, 10) : null,
              workHours1: data.paymentPeriod1.workHours ? parseInt(data.paymentPeriod1.workHours, 10) : null,
              salaryAmount1: data.paymentPeriod1.salaryAmount ? parseInt(data.paymentPeriod1.salaryAmount, 10) : null,
              startOn2: blankToNull(data.paymentPeriod2.startOn),
              endOn2: blankToNull(data.paymentPeriod2.endOn),
              workDays2: data.paymentPeriod2.workDays ? parseInt(data.paymentPeriod2.workDays, 10) : null,
              workHours2: data.paymentPeriod2.workHours ? parseInt(data.paymentPeriod2.workHours, 10) : null,
              salaryAmount2: data.paymentPeriod2.salaryAmount ? parseInt(data.paymentPeriod2.salaryAmount, 10) : null,
              startOn3: blankToNull(data.paymentPeriod3.startOn),
              endOn3: blankToNull(data.paymentPeriod3.endOn),
              workDays3: data.paymentPeriod3.workDays ? parseInt(data.paymentPeriod3.workDays, 10) : null,
              workHours3: data.paymentPeriod3.workHours ? parseInt(data.paymentPeriod3.workHours, 10) : null,
              salaryAmount3: data.paymentPeriod3.salaryAmount ? parseInt(data.paymentPeriod3.salaryAmount, 10) : null,
              reasonForExtendingPaymentPeriod: data.extendingPaymentPeriod.reasonForExtendingPaymentPeriod,
              extendedPaymentPeriodStartOn: blankToNull(data.extendingPaymentPeriod.extendedPaymentPeriodStartOn),
              extendedPaymentPeriodEndOn: blankToNull(data.extendingPaymentPeriod.extendedPaymentPeriodEndOn),
              hasCommutingExpenses: data.remarks.hasCommutingExpenses === 'true',
              commutingExpensePaymentDurationUnit: blankToNull(data.remarks.commutingExpensePaymentDurationUnit),
              commutingExpensePaymentDurationUnitOtherDetail:
                data.remarks.commutingExpensePaymentDurationUnitOtherDetail,
              hasContractPeriod: data.remarks.hasContractPeriod === 'true',
              contractPeriodEndOn: blankToNull(data.remarks.contractPeriodEndOn),
              notes: data.remarks.notes,
              needLaborConsultant: !!data.laborConsultantForm.needLaborConsultant,
              laborConsultantCreateDate: blankToNull(data.laborConsultantForm.laborConsultantCreateDate),
              laborConsultantName: data.laborConsultantForm.laborConsultantName,
              laborConsultantAgentType: data.laborConsultantForm.laborConsultantAgentType,
              laborConsultantPrefectureId: data.laborConsultantForm.laborConsultantPrefectureId,
              laborConsultantPhoneNumber0: data.laborConsultantForm.laborConsultantPhoneNumber0,
              laborConsultantPhoneNumber1: data.laborConsultantForm.laborConsultantPhoneNumber1,
              laborConsultantPhoneNumber2: data.laborConsultantForm.laborConsultantPhoneNumber2,
              version: procedureStatus.r511ParamSet.version,
            },
          },
        },
      })
      await updateProcedureStatus({
        variables: {
          input: {
            id: procedureStatusId,
            initialized: true,
          },
        },
      })
    } catch (err) {
      dispatch(asyncError(err))
      return
    }

    return await afterSubmit()
  }

  return (
    <div>
      <div className="l-title-wrap">
        <h1 className="m-title-main">{titleMap[procedureStatus.procedureType]}</h1>
        <EmployeeName employee={procedureStatus.employee} />
      </div>
      <Form
        procedureStatus={procedureStatus}
        submitText={submitText}
        onSubmit={onSubmit}
        procedureContext={procedureContext}
        comments={comments}
        renderReject={renderReject}
      />
    </div>
  )
}

const reasonForExtendingPaymentPeriods = [
  {value: '', label: ''},
  {value: 'nursery_care_not_implemented', label: '1. 保育所における保育が実施されないこと'},
  {value: 'spouse_passed_away', label: '2. 養育を予定していた配偶者の死亡'},
  {value: 'spouse_injury_or_illness', label: '3. 養育を予定していた配偶者の負傷・疾病等'},
  {value: 'divorced', label: '4. 養育を予定していた配偶者との婚姻の解消等による別居'},
  {value: 'spouse_maternity_leave', label: '5. 養育を予定していた配偶者の産前産後休業等'},
  {value: 'other_reason_no_longer_exists', label: '6. 他休業事由の消滅'},
]

const commutingExpensePaymentDurationUnits = [
  {value: '', label: ''},
  {value: 'per_month', label: '毎月'},
  {value: 'per_3months', label: '3ヶ月'},
  {value: 'per_6months', label: '6ヶ月'},
  {value: 'other', label: 'その他'},
]

const paymentPeriodSchema = (prefix: string, dateRequired: boolean = false) =>
  yup.object({
    startOn: ((schema) => (dateRequired ? schema.required() : schema.notRequired()))(
      yup.string().dateFormat().label(`${prefix} 初日`)
    ),
    endOn: ((schema) => (dateRequired ? schema.required() : schema.notRequired()))(
      yup.string().dateFormat().label(`${prefix} 末日`)
    ),
    workDays: yup
      .string()
      .notRequired()
      .matches(/^\d{1,2}$/, {
        message: ({label}) => `${label}を2桁以内の半角数字で入力してください`,
        excludeEmptyString: true,
      })
      .label(`${prefix} 就業日数`),
    workHours: yup
      .string()
      .notRequired()
      .matches(/^\d{1,3}$/, {
        message: ({label}) => `${label}を3桁以内の半角数字で入力してください`,
        excludeEmptyString: true,
      })
      .label(`${prefix} 就業時間`),
    salaryAmount: yup
      .string()
      .notRequired()
      .numeric()
      .test(
        'salaryAmountMax',
        ({label}) => `${label}を9,999,999円以下で入力してください`,
        (value) => !value || parseInt(value, 10) <= 9999999
      )
      .label(`${prefix} 支払われた賃金額`),
  })

const schema = yup.object({
  startedOn: yup.string().required().dateFormat().label('育児休業等開始年月日'),
  birthDate: yup.string().notRequired().dateFormat().label('出産年月日'),
  expectedBirthOn: yup.string().notRequired().dateFormat().label('出産予定日'),
  paymentPeriod1: paymentPeriodSchema('支給単位期間その1', true).required(),
  paymentPeriod2: paymentPeriodSchema('支給単位期間その2').required(),
  paymentPeriod3: paymentPeriodSchema('最終支給単位期間').required(),
  returnToWorkOn: yup.string().notRequired().dateFormat().label('職場復帰年月日'),
  extendingPaymentPeriod: yup.object({
    reasonForExtendingPaymentPeriod: yup
      .string()
      .notRequired()
      .emptyToNull()
      .label('支給対象となる期間の延長事由ー期間の事由'),
    extendedPaymentPeriodStartOn: yup
      .string()
      .notRequired()
      .dateFormat()
      .label('支給対象となる期間の延長事由ー期間の初日'),
    extendedPaymentPeriodEndOn: yup
      .string()
      .notRequired()
      .dateFormat()
      .label('支給対象となる期間の延長事由ー期間の末日'),
  }),
  isPapaMamaLeavePlus: yup.string().notRequired().oneOf(['true', 'false']).label('パパ・ママ育休プラス制度活用'),
  spouseInsurancePersonNumber0: yup
    .string()
    .notRequired()
    .numeric()
    .matches(/^\d{4}$/, {
      message: ({label}) => `${label}を4桁の半角数字で入力してください`,
      excludeEmptyString: true,
    })
    .label('配偶者の被保険者番号の1番'),
  spouseInsurancePersonNumber1: yup
    .string()
    .notRequired()
    .numeric()
    .matches(/^\d{6}$/, {
      message: ({label}) => `${label}を6桁の半角数字で入力してください`,
      excludeEmptyString: true,
    })
    .label('配偶者の被保険者番号の2番'),
  spouseInsurancePersonNumber2: yup
    .string()
    .notRequired()
    .numeric()
    .matches(/^\d$/, {
      message: ({label}) => `${label}を1桁の半角数字で入力してください`,
      excludeEmptyString: true,
    })
    .label('配偶者の被保険者番号の3番'),
  specialReport1: yup.string().notRequired().max(40).label('その他賃金に関する特記事項1'),
  specialReport2: yup.string().notRequired().max(40).label('その他賃金に関する特記事項2'),
  remarks: yup
    .object({
      hasCommutingExpenses: yup.string().notRequired().oneOf(['true', 'false']).label('通勤手当の有無'),
      commutingExpensePaymentDurationUnit: yup.string().notRequired().label('通勤手当の支給単位'),
      commutingExpensePaymentDurationUnitOtherDetail: yup
        .string()
        .notRequired()
        .max(4)
        .label('通勤手当の支給単位 その他の内容'),
      hasContractPeriod: yup.string().notRequired().oneOf(['true', 'false']).label('雇用期間の定めの有無'),
      contractPeriodEndOn: yup.string().notRequired().dateFormat().label('雇用期間終了日'),
      notes: yup.string().notRequired().max(68).label('備考'),
      ownerCertifiedOn: yup.string().notRequired().dateFormat().label('事業主証明年月日'),
      submitOn: yup.string().notRequired().dateFormat().label('提出年月日'),
      isApplicantConsented: yup.boolean().notRequired().label('申請について同意済み'),
    })
    .required(),
  laborConsultantForm: laborConsultantSchema.required(),
})

type Schema = yup.InferType<typeof schema>

const makeDefaultValues = (ps: R511ReportQueryResult['client']['procedureStatus']): Schema => {
  const radioBooleanFormat = (value: boolean | undefined | null): 'true' | 'false' | undefined => {
    if (value === undefined || value === null) return undefined
    return value ? 'true' : 'false'
  }

  const r511 = ps.r511ParamSet

  return {
    startedOn: r511?.startedOn ? dateFormat(r511.startedOn, 'YYYY/MM/DD') : '',
    birthDate: r511?.birthDate ? dateFormat(r511.birthDate, 'YYYY/MM/DD') : '',
    paymentPeriod1: {
      startOn: r511.startOn1 ? dateFormat(r511.startOn1, 'YYYY/MM/DD') : '',
      endOn: r511.endOn1 ? dateFormat(r511.endOn1, 'YYYY/MM/DD') : '',
      workDays: String(r511.workDays1 ?? ''),
      workHours: String(r511.workHours1 ?? ''),
      salaryAmount: String(r511.salaryAmount1 ?? ''),
    },
    paymentPeriod2: {
      startOn: r511.startOn2 ? dateFormat(r511.startOn2, 'YYYY/MM/DD') : '',
      endOn: r511.endOn2 ? dateFormat(r511.endOn2, 'YYYY/MM/DD') : '',
      workDays: String(r511.workDays2 ?? ''),
      workHours: String(r511.workHours2 ?? ''),
      salaryAmount: String(r511.salaryAmount2 ?? ''),
    },
    paymentPeriod3: {
      startOn: r511.startOn3 ? dateFormat(r511.startOn3, 'YYYY/MM/DD') : '',
      endOn: r511.endOn3 ? dateFormat(r511.endOn3, 'YYYY/MM/DD') : '',
      workDays: String(r511.workDays3 ?? ''),
      workHours: String(r511.workHours3 ?? ''),
      salaryAmount: String(r511.salaryAmount3 ?? ''),
    },
    returnToWorkOn: r511.returnToWorkOn ? dateFormat(r511.returnToWorkOn, 'YYYY/MM/DD') : '',
    extendingPaymentPeriod: {
      reasonForExtendingPaymentPeriod: r511.reasonForExtendingPaymentPeriod,
      extendedPaymentPeriodStartOn: r511.extendedPaymentPeriodStartOn
        ? dateFormat(r511.extendedPaymentPeriodStartOn, 'YYYY/MM/DD')
        : '',
      extendedPaymentPeriodEndOn: r511.extendedPaymentPeriodEndOn
        ? dateFormat(r511.extendedPaymentPeriodEndOn, 'YYYY/MM/DD')
        : '',
    },
    isPapaMamaLeavePlus: radioBooleanFormat(r511.isPapaMamaLeavePlus),
    spouseInsurancePersonNumber0: r511.spouseInsurancePersonNumber0,
    spouseInsurancePersonNumber1: r511.spouseInsurancePersonNumber1,
    spouseInsurancePersonNumber2: r511.spouseInsurancePersonNumber2,
    specialReport1: r511.specialReport1,
    specialReport2: r511.specialReport2,
    remarks: {
      hasCommutingExpenses: radioBooleanFormat(r511.hasCommutingExpenses),
      commutingExpensePaymentDurationUnit: r511.commutingExpensePaymentDurationUnit,
      commutingExpensePaymentDurationUnitOtherDetail: r511.commutingExpensePaymentDurationUnitOtherDetail,
      hasContractPeriod: radioBooleanFormat(r511.hasContractPeriod),
      contractPeriodEndOn: r511.contractPeriodEndOn ? dateFormat(r511.contractPeriodEndOn, 'YYYY/MM/DD') : '',
      notes: r511.notes,
      ownerCertifiedOn: r511.ownerCertifiedOn ? dateFormat(r511.ownerCertifiedOn, 'YYYY/MM/DD') : '',
      submitOn: r511.submitOn ? dateFormat(r511.submitOn, 'YYYY/MM/DD') : '',
      isApplicantConsented: r511.isApplicantConsented,
    },
    laborConsultantForm: {
      needLaborConsultant: r511.needLaborConsultant,
      laborConsultantCreateDate: r511.laborConsultantCreateDate
        ? dateFormat(r511.laborConsultantCreateDate, 'YYYY/MM/DD')
        : '',
      laborConsultantName: r511.laborConsultantName || '',
      laborConsultantAgentType: r511.laborConsultantAgentType || undefined,
      laborConsultantPrefectureId: r511.laborConsultantPrefecture?.id || undefined,
      laborConsultantPhoneNumber0: r511.laborConsultantPhoneNumber0 || '',
      laborConsultantPhoneNumber1: r511.laborConsultantPhoneNumber1 || '',
      laborConsultantPhoneNumber2: r511.laborConsultantPhoneNumber2 || '',
    },
  }
}

interface FormProps {
  procedureStatus: R511ReportQueryResult['client']['procedureStatus']
  submitText: string
  onSubmit: SubmitHandler<Schema>
  procedureContext: ProcedureContext
  comments?: ReactNode
  renderReject?: ReportFormProps['renderReject']
}

const Form: FC<FormProps> = ({procedureStatus, submitText, onSubmit, procedureContext, comments, renderReject}) => {
  const methods = useForm<Schema>({
    defaultValues: makeDefaultValues(procedureStatus),
    resolver: yupResolver(schema),
    mode: 'onTouched',
    shouldFocusError: false,
    context: {procedureContext},
  })
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: {errors, isSubmitting},
  } = methods
  const isPapaMamaLeavePlus = watch('isPapaMamaLeavePlus') === 'true'
  const hasCommutingExpenses = watch('remarks.hasCommutingExpenses') === 'true'
  const commutingExpensePaymentDurationUnit = watch('remarks.commutingExpensePaymentDurationUnit')
  const hasContractPeriod = watch('remarks.hasContractPeriod') === 'true'

  // MethodSelect で「過去の手続き情報を利用する」を選択した場合のみ、画面上部に
  // 「過去の手続きを選択」section を表示する（UI は ChildcareInfoForm の from_past section と同形）
  const isFromPast = procedureStatus.childcareMethodSelect?.data?.method === 'from_past'
  const {data: pastData} = useQuery<
    PastChildcareProceduresForReflectQueryResult,
    PastChildcareProceduresForReflectQueryVariables
  >(PAST_CHILDCARE_PROCEDURES_FOR_REFLECT, {
    variables: {procedureStatusId: procedureStatus.id, forProcedureType: procedureStatus.procedureType},
    skip: !isFromPast,
    fetchPolicy: 'network-only',
  })
  const pastProcedures = pastData?.client?.procedureStatus?.employee.pastChildcareProcedureStatuses ?? []
  const pastOptions = pastProcedures.map((p) => {
    const started = p.procedureChildcareDatum?.startedOn
      ? dateFormat(p.procedureChildcareDatum.startedOn, 'YYYY/MM/DD')
      : ''
    const c = p.procedureChildcareDatum?.procedureChildcareChildDatum
    const childName = c?.lastName && c?.firstName ? fullNameFormat(c.lastName, c.firstName) : ''
    const label = childName ? `育休開始日 ${started}\u{3000}子: ${childName}` : `育休開始日 ${started}`
    return {value: p.id, label}
  })
  const [selectedPastId, setSelectedPastId] = useState<string | undefined>()
  // 初期選択は先頭（最直近）。バックエンドが date_on DESC で返すため先頭が最新。
  const currentPastId = selectedPastId ?? pastProcedures[0]?.id

  // 「手続きから情報を反映する」— R511 は「育児休業等開始年月日」「出産年月日」のみ反映
  const handleReflectFromPast = () => {
    const datum = pastProcedures.find((p) => p.id === currentPastId)?.procedureChildcareDatum
    if (!datum) {
      return
    }
    const child = datum.procedureChildcareChildDatum
    const opt = {shouldDirty: true}
    setValue('startedOn', datum.startedOn ? dateFormat(datum.startedOn, 'YYYY/MM/DD') : '', opt)
    setValue('birthDate', child?.birthDate ? dateFormat(child.birthDate, 'YYYY/MM/DD') : '', opt)
  }

  const handleSubmitError = () => {
    window.scrollTo({top: 0, behavior: 'smooth'})
  }
  const {confirmUpgrade, confirmUpgradeModal} = useConfirmUpgrade(
    procedureStatus.r511ParamSet.needUpgrade,
    handleSubmit(onSubmit, handleSubmitError)
  )

  useEffect(() => {
    if (!isPapaMamaLeavePlus) {
      const options = {shouldTouch: true, shouldDirty: true, shouldValidate: true}
      setValue('spouseInsurancePersonNumber0', '', options)
      setValue('spouseInsurancePersonNumber1', '', options)
      setValue('spouseInsurancePersonNumber2', '', options)
    }
  }, [isPapaMamaLeavePlus, setValue])

  useEffect(() => {
    if (!hasCommutingExpenses) {
      const options = {shouldTouch: true, shouldDirty: true, shouldValidate: true}
      setValue('remarks.commutingExpensePaymentDurationUnit', '', options)
      setValue('remarks.commutingExpensePaymentDurationUnitOtherDetail', '', options)
    }
  }, [hasCommutingExpenses, setValue])

  useEffect(() => {
    if (commutingExpensePaymentDurationUnit !== 'other') {
      setValue('remarks.commutingExpensePaymentDurationUnitOtherDetail', '', {
        shouldTouch: true,
        shouldDirty: true,
        shouldValidate: true,
      })
    }
  }, [commutingExpensePaymentDurationUnit, setValue])

  useEffect(() => {
    if (!hasContractPeriod) {
      setValue('remarks.contractPeriodEndOn', '', {shouldTouch: true, shouldDirty: true, shouldValidate: true})
    }
  }, [hasContractPeriod, setValue])

  return (
    <FormProvider {...methods}>
      <div className="basic_information l-wrap-xs">
        <FormErrors errors={errors} isSubmitting={isSubmitting} />
        {comments}

        {isFromPast && (
          <Panel className="u-mb20">
            <Head>過去の手続きを選択</Head>
            <Body className={styles.formBody}>
              <Note>過去に作成した「育休の開始」から「休業期間」の情報を反映することができます。</Note>
              <Select
                options={pastOptions}
                value={pastOptions.find((option) => option.value === currentPastId)}
                onChange={(option) => setSelectedPastId(option?.value)}
                placeholder="選択してください"
              />
              <div>
                <Button onClick={handleReflectFromPast} disabled={!currentPastId}>
                  手続きから情報を反映する
                </Button>
              </div>
            </Body>
          </Panel>
        )}

        <Panel>
          <Head>育児休業給付金／出生後休業支援給付金支給申請書</Head>
          <Body className={styles.formBody}>
            <DateField name="startedOn" label="育児休業等開始年月日" required error={errors.startedOn?.message} />
            <DateField name="birthDate" label="出産年月日" error={errors.birthDate?.message} />

            <Panel>
              <Head>支給単位期間その1</Head>
              <Body className={styles.formBody}>
                <DateField
                  name="paymentPeriod1.startOn"
                  label="初日"
                  error={errors.paymentPeriod1?.startOn?.message}
                  required
                />
                <DateField
                  name="paymentPeriod1.endOn"
                  label="末日"
                  error={errors.paymentPeriod1?.endOn?.message}
                  required
                />
                <div className={styles.field}>
                  <Label>就業日数</Label>
                  <InputWithText
                    {...register('paymentPeriod1.workDays')}
                    text="日"
                    isError={!!errors.paymentPeriod1?.workDays}
                  />
                  {errors.paymentPeriod1?.workDays?.message && (
                    <ErrorMessage>{errors.paymentPeriod1.workDays.message}</ErrorMessage>
                  )}
                </div>
                <div className={styles.field}>
                  <Label>就業時間</Label>
                  <InputWithText
                    {...register('paymentPeriod1.workHours')}
                    text="時間"
                    isError={!!errors.paymentPeriod1?.workHours}
                  />
                  {errors.paymentPeriod1?.workHours?.message && (
                    <ErrorMessage>{errors.paymentPeriod1.workHours.message}</ErrorMessage>
                  )}
                </div>
                <div className={styles.field}>
                  <Label>支払われた賃金額</Label>
                  <InputWithText
                    {...register('paymentPeriod1.salaryAmount')}
                    text="円"
                    isError={!!errors.paymentPeriod1?.salaryAmount}
                  />
                  {errors.paymentPeriod1?.salaryAmount?.message && (
                    <ErrorMessage>{errors.paymentPeriod1.salaryAmount.message}</ErrorMessage>
                  )}
                </div>
              </Body>
            </Panel>

            <Panel>
              <Head>支給単位期間その2</Head>
              <Body className={styles.formBody}>
                <DateField name="paymentPeriod2.startOn" label="初日" error={errors.paymentPeriod2?.startOn?.message} />
                <DateField name="paymentPeriod2.endOn" label="末日" error={errors.paymentPeriod2?.endOn?.message} />
                <div className={styles.field}>
                  <Label>就業日数</Label>
                  <InputWithText
                    {...register('paymentPeriod2.workDays')}
                    text="日"
                    isError={!!errors.paymentPeriod2?.workDays}
                  />
                  {errors.paymentPeriod2?.workDays?.message && (
                    <ErrorMessage>{errors.paymentPeriod2.workDays.message}</ErrorMessage>
                  )}
                </div>
                <div className={styles.field}>
                  <Label>就業時間</Label>
                  <InputWithText
                    {...register('paymentPeriod2.workHours')}
                    text="時間"
                    isError={!!errors.paymentPeriod2?.workHours}
                  />
                  {errors.paymentPeriod2?.workHours?.message && (
                    <ErrorMessage>{errors.paymentPeriod2.workHours.message}</ErrorMessage>
                  )}
                </div>
                <div className={styles.field}>
                  <Label>支払われた賃金額</Label>
                  <InputWithText
                    {...register('paymentPeriod2.salaryAmount')}
                    text="円"
                    isError={!!errors.paymentPeriod2?.salaryAmount}
                  />
                  {errors.paymentPeriod2?.salaryAmount?.message && (
                    <ErrorMessage>{errors.paymentPeriod2.salaryAmount.message}</ErrorMessage>
                  )}
                </div>
              </Body>
            </Panel>

            <Panel>
              <Head>最終支給単位期間</Head>
              <Body className={styles.formBody}>
                <DateField name="paymentPeriod3.startOn" label="初日" error={errors.paymentPeriod3?.startOn?.message} />
                <DateField name="paymentPeriod3.endOn" label="末日" error={errors.paymentPeriod3?.endOn?.message} />
                <div className={styles.field}>
                  <Label>就業日数</Label>
                  <InputWithText
                    {...register('paymentPeriod3.workDays')}
                    text="日"
                    isError={!!errors.paymentPeriod3?.workDays}
                  />
                  {errors.paymentPeriod3?.workDays?.message && (
                    <ErrorMessage>{errors.paymentPeriod3.workDays.message}</ErrorMessage>
                  )}
                </div>
                <div className={styles.field}>
                  <Label>就業時間</Label>
                  <InputWithText
                    {...register('paymentPeriod3.workHours')}
                    text="時間"
                    isError={!!errors.paymentPeriod3?.workHours}
                  />
                  {errors.paymentPeriod3?.workHours?.message && (
                    <ErrorMessage>{errors.paymentPeriod3.workHours.message}</ErrorMessage>
                  )}
                </div>
                <div className={styles.field}>
                  <Label>支払われた賃金額</Label>
                  <InputWithText
                    {...register('paymentPeriod3.salaryAmount')}
                    text="円"
                    isError={!!errors.paymentPeriod3?.salaryAmount}
                  />
                  {errors.paymentPeriod3?.salaryAmount?.message && (
                    <ErrorMessage>{errors.paymentPeriod3.salaryAmount.message}</ErrorMessage>
                  )}
                </div>
              </Body>
            </Panel>

            <DateField name="returnToWorkOn" label="職場復帰年月日" error={errors.returnToWorkOn?.message} />

            <Panel>
              <Head>支給対象となる期間の延長事由ー期間</Head>
              <Body className={styles.formBody}>
                <div className={styles.field}>
                  <Label>事由</Label>
                  <Controller
                    control={control}
                    name="extendingPaymentPeriod.reasonForExtendingPaymentPeriod"
                    render={({field: {onChange, onBlur, value}}) => (
                      <Select
                        options={reasonForExtendingPaymentPeriods}
                        onChange={({value}) => onChange(value)}
                        onBlur={onBlur}
                        value={reasonForExtendingPaymentPeriods.find((option) => option.value === value)}
                        placeholder="選択してください"
                      />
                    )}
                  />
                </div>
                <DateField
                  name="extendingPaymentPeriod.extendedPaymentPeriodStartOn"
                  label="初日"
                  error={errors.extendingPaymentPeriod?.extendedPaymentPeriodStartOn?.message}
                />
                <DateField
                  name="extendingPaymentPeriod.extendedPaymentPeriodEndOn"
                  label="末日"
                  error={errors.extendingPaymentPeriod?.extendedPaymentPeriodEndOn?.message}
                />
              </Body>
            </Panel>

            <div className={styles.field}>
              <Label>パパ・ママ育休プラス制度活用</Label>
              <RadioContainer>
                <Radio {...register('isPapaMamaLeavePlus')} value="false">
                  なし
                </Radio>
                <Radio {...register('isPapaMamaLeavePlus')} value="true">
                  あり
                </Radio>
              </RadioContainer>
            </div>
            <div className={styles.field}>
              <Label>配偶者の被保険者番号</Label>
              <div className={styles.personNumber}>
                <Input
                  className={styles.personNumberSegment1}
                  {...register('spouseInsurancePersonNumber0')}
                  maxLength={4}
                  isError={!!errors.spouseInsurancePersonNumber0}
                  disabled={!isPapaMamaLeavePlus}
                />
                -
                <Input
                  className={styles.personNumberSegment2}
                  {...register('spouseInsurancePersonNumber1')}
                  maxLength={6}
                  isError={!!errors.spouseInsurancePersonNumber1}
                  disabled={!isPapaMamaLeavePlus}
                />
                -
                <Input
                  className={styles.personNumberSegment3}
                  {...register('spouseInsurancePersonNumber2')}
                  maxLength={1}
                  isError={!!errors.spouseInsurancePersonNumber2}
                  disabled={!isPapaMamaLeavePlus}
                />
              </div>
              {(() => {
                const personNumberErrors = [
                  errors.spouseInsurancePersonNumber0?.message,
                  errors.spouseInsurancePersonNumber1?.message,
                  errors.spouseInsurancePersonNumber2?.message,
                ].filter((v) => v !== undefined)
                return (
                  personNumberErrors.length > 0 && (
                    <div>
                      {personNumberErrors.map((error, index) => (
                        <ErrorMessage key={index}>{error}</ErrorMessage>
                      ))}
                    </div>
                  )
                )
              })()}
            </div>

            <div className={styles.field}>
              <Label>その他賃金に関する特記事項1</Label>
              <Input {...register('specialReport1')} isError={!!errors.specialReport1} />
              {errors.specialReport1?.message && <ErrorMessage>{errors.specialReport1.message}</ErrorMessage>}
            </div>
            <div className={styles.field}>
              <Label>その他賃金に関する特記事項2</Label>
              <Input {...register('specialReport2')} isError={!!errors.specialReport2} />
              {errors.specialReport2?.message && <ErrorMessage>{errors.specialReport2.message}</ErrorMessage>}
            </div>

            <Panel>
              <Head>備考</Head>
              <Body className={styles.formBody}>
                <Panel>
                  <Head>通勤手当</Head>
                  <Body className={styles.formBody}>
                    <div className={styles.field}>
                      <Label>通勤手当の有無</Label>
                      <RadioContainer>
                        <Radio {...register('remarks.hasCommutingExpenses')} value="true">
                          あり
                        </Radio>
                        <Radio {...register('remarks.hasCommutingExpenses')} value="false">
                          なし
                        </Radio>
                      </RadioContainer>
                    </div>
                    <div className={styles.field}>
                      <Label>通勤手当の支給単位</Label>
                      <Controller
                        control={control}
                        name="remarks.commutingExpensePaymentDurationUnit"
                        render={({field: {onChange, onBlur, value}}) => (
                          <Select
                            options={commutingExpensePaymentDurationUnits}
                            onChange={({value}) => onChange(value)}
                            onBlur={onBlur}
                            value={commutingExpensePaymentDurationUnits.find((option) => option.value === value)}
                            placeholder="選択してください"
                            disabled={watch('remarks.hasCommutingExpenses') == 'false'}
                          />
                        )}
                      />
                    </div>
                    <div className={styles.field}>
                      <Label>通勤手当のその他の内容</Label>
                      <Input
                        {...register('remarks.commutingExpensePaymentDurationUnitOtherDetail')}
                        isError={!!errors.remarks?.commutingExpensePaymentDurationUnitOtherDetail}
                        disabled={watch('remarks.commutingExpensePaymentDurationUnit') !== 'other'}
                      />
                      {errors.remarks?.commutingExpensePaymentDurationUnitOtherDetail?.message && (
                        <ErrorMessage>
                          {errors.remarks.commutingExpensePaymentDurationUnitOtherDetail.message}
                        </ErrorMessage>
                      )}
                    </div>
                  </Body>
                </Panel>

                <Panel>
                  <Head>休業開始時における雇用期間</Head>
                  <Body className={styles.formBody}>
                    <div className={styles.field}>
                      <Label>雇用期間の定めの有無</Label>
                      <RadioContainer>
                        <Radio {...register('remarks.hasContractPeriod')} value="true">
                          あり
                        </Radio>
                        <Radio {...register('remarks.hasContractPeriod')} value="false">
                          なし
                        </Radio>
                      </RadioContainer>
                    </div>
                    <DateField
                      name="remarks.contractPeriodEndOn"
                      label="雇用期間終了日"
                      error={errors.remarks?.contractPeriodEndOn?.message}
                      disabled={!hasContractPeriod}
                    />
                  </Body>
                </Panel>

                <div className={styles.field}>
                  <Label>備考欄</Label>
                  <Textarea {...register('remarks.notes')} isError={!!errors.remarks?.notes} />
                  {errors.remarks?.notes?.message && <ErrorMessage>{errors.remarks.notes.message}</ErrorMessage>}
                </div>
              </Body>
            </Panel>

            <DateField
              name="remarks.ownerCertifiedOn"
              label="事業主証明年月日"
              error={errors.remarks?.ownerCertifiedOn?.message}
            />

            <Panel>
              <Head>申請者欄</Head>
              <Body className={styles.formBody}>
                <DateField name="remarks.submitOn" label="提出年月日" error={errors.remarks?.submitOn?.message} />

                <div className={styles.field}>
                  <Label>申請者氏名</Label>
                  <Note>
                    同意書を有する場合等、被保険者の（電子）署名が省略できる場合には以下にチェックを入れてください。
                  </Note>
                  <Checkbox {...register('remarks.isApplicantConsented')}>申請について同意済み</Checkbox>
                </div>
              </Body>
            </Panel>

            <LaborConsultantSection
              procedureStatusId={procedureStatus.id}
              reportName="R511"
              prefix="laborConsultantForm."
            />
          </Body>
        </Panel>

        <div className="u-ta-c u-mt30">
          {renderReject?.({isSubmitting})}
          <Button primary onClick={confirmUpgrade} disabled={isSubmitting}>
            {submitText}
          </Button>
        </div>
      </div>

      {confirmUpgradeModal}
    </FormProvider>
  )
}
